import React from 'react';
import { supabase } from '../lib/supabaseClient';
import type { AppNotification } from '../types/notifications';

type NotificationsResponse = {
  notifications: AppNotification[];
  unread_count: number;
};

type UseNotificationsOptions = {
  limit?: number;
  /** Archiv statt Posteingang laden. */
  archived?: boolean;
};

export function useNotifications(
  userId: string | null,
  instanceId: string | null,
  options?: UseNotificationsOptions,
) {
  const limit = options?.limit ?? 20;
  const archived = options?.archived ?? false;
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!userId) return;
      if (!opts?.silent) setLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(limit) });
        if (archived) params.set('archived', 'true');
        const res = await fetch(`/api/notifications?${params}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = (await res.json()) as NotificationsResponse;
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUnreadCount(typeof data.unread_count === 'number' ? data.unread_count : 0);
      } catch {
        // ignore
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [archived, limit, userId],
  );

  React.useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    void refresh();
  }, [userId, instanceId, refresh]);

  React.useEffect(() => {
    if (!userId || !instanceId) return;
    const onChange = (payload: { new?: unknown }) => {
      const row = (payload.new ?? {}) as { instance_id?: string };
      if (String(row.instance_id ?? '') !== instanceId) return;
      void refresh({ silent: true });
    };
    const filter = `recipient_user_id=eq.${userId}`;
    const channel = supabase
      .channel(`notifications:${userId}:${instanceId}:${archived ? 'archive' : 'inbox'}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter }, onChange)
      // Archivieren (auch automatisch bei erledigten Anträgen oder in einem anderen Tab) ist ein UPDATE.
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, instanceId, archived, refresh]);

  const markRead = React.useCallback(async (id: string) => {
    const res = await fetch(`/api/notifications/${encodeURIComponent(id)}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    return true;
  }, []);

  const markAllRead = React.useCallback(async () => {
    const res = await fetch('/api/notifications/read-all', {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
    return true;
  }, []);

  /** Archivieren bzw. Wiederherstellen: Eintrag verschwindet sofort aus der aktuellen Liste. */
  const setArchived = React.useCallback(
    async (notification: AppNotification, archive: boolean) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      if (archive && !notification.is_read) setUnreadCount((c) => Math.max(0, c - 1));
      const res = await fetch(
        `/api/notifications/${encodeURIComponent(notification.id)}/${archive ? 'archive' : 'unarchive'}`,
        { method: 'POST', credentials: 'include' },
      ).catch(() => null);
      if (!res?.ok) {
        void refresh({ silent: true });
        return false;
      }
      return true;
    },
    [refresh],
  );

  const archive = React.useCallback((n: AppNotification) => setArchived(n, true), [setArchived]);

  /** Rückgängig nach dem Archivieren oder „Wiederherstellen“ im Archiv. */
  const unarchive = React.useCallback(
    async (n: AppNotification) => {
      const ok = await setArchived(n, false);
      // Im Posteingang wieder an der richtigen Stelle anzeigen.
      if (ok && !archived) void refresh({ silent: true });
      return ok;
    },
    [archived, refresh, setArchived],
  );

  const archiveAll = React.useCallback(async () => {
    const res = await fetch('/api/notifications/archive-all', {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    setNotifications([]);
    setUnreadCount(0);
    return true;
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
    archive,
    unarchive,
    archiveAll,
  };
}
